import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import inventoryItemService from '../../services/inventoryItemService';
import type { InventoryItemWithBalance } from '../../services/inventoryItemService';
import venueLocationService, { pickDefaultLocation, locationLabel } from '../../services/venueLocationService';
import type { VenueLocation } from '../../types/venue';
import wastageService, { extractWastageError } from '../../services/wastageService';

/**
 * WASTAGE.1 — record stock that is gone but was not sold.
 *
 * Breakage, spoilage, a corked bottle, staff training. Distinct from a stock
 * count (which discovers a discrepancy after the fact and cannot say why) and
 * from a manual adjustment (which records that the number changed, also without
 * a why). Both leave the loss as unexplained shrinkage; this records the reason
 * at the moment someone knows it.
 *
 * The reason is REQUIRED and is stored as a value reports group by. Free text
 * alone does not aggregate, which is why venues ended up encoding wastage in
 * payment methods and table names — anything that reports.
 *
 * Quantity accepts fractions. Choosing a by-the-glass RECIPE item wastes
 * SERVINGS, and the backend depletes the components: one 150ml pour takes 0.2
 * off the bottle.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  venueId: string;
}

/**
 * Starter reasons for venues with nothing configured yet.
 *
 * Deliberately named by what HAPPENED, not by accounting treatment: whether
 * staff training belongs in a waste line or a marketing line is a P&L decision,
 * and an operator standing over a bottle should not have to make it. A free
 * text option stays available for anything this list does not cover.
 */
const DEFAULT_REASONS = [
  'Breakage',
  'Spoilage',
  'Corked',
  'Guest complaint',
  'Staff training / tasting',
  'Over-pour',
  'Other',
];

const RecordWastageModal: React.FC<Props> = ({ open, onClose, onSuccess, venueId }) => {
  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationId, setLocationId] = useState('');

  const [item, setItem] = useState<InventoryItemWithBalance | null>(null);
  const [searchResults, setSearchResults] = useState<InventoryItemWithBalance[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Reset on open so a previous write-off never pre-fills the next ─────────
  useEffect(() => {
    if (!open) return;
    setItem(null);
    setSearchResults([]);
    setQuantity('');
    setReason('');
    setNotes('');
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open || !venueId) return;
    setLocationsLoading(true);
    venueLocationService.getLocations(venueId)
      .then(locs => {
        const active = locs.filter(l => l.active);
        setLocations(active);
        const def = pickDefaultLocation(active);
        if (def) setLocationId(def._id);
      })
      .catch(() => setLocations([]))
      .finally(() => setLocationsLoading(false));
  }, [open, venueId]);

  const handleSearch = (input: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!input || input.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await inventoryItemService.getItems(venueId, 1, 20, 'displayName', 'asc', input);
        setSearchResults(res.data ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const isRecipe = item?.executionBehavior === 'RECIPE_CONSUMPTION';
  const quantityNum = quantity !== '' ? Number(quantity) : NaN;
  const quantityValid = Number.isFinite(quantityNum) && quantityNum > 0;
  const canSubmit = Boolean(locationId && item && quantityValid && reason.trim()) && !submitting;

  /**
   * What the operator is about to remove, in the units they think in.
   *
   * A recipe's quantity is servings, and the stock that actually moves is a
   * fraction of a bottle — worth saying out loud before they commit, because
   * "1" means something different on this row than on any other.
   */
  const effectHint = useMemo(() => {
    if (!item || !quantityValid) return null;
    if (isRecipe) {
      return `${quantityNum} serving${quantityNum === 1 ? '' : 's'} — depletes this recipe's ingredients.`;
    }
    return `${quantityNum} × ${item.displayName}`;
  }, [item, isRecipe, quantityNum, quantityValid]);

  const handleSubmit = async () => {
    if (!canSubmit || !item) return;
    setSubmitting(true);
    setError(null);
    try {
      await wastageService.postWastage(venueId, {
        locationId,
        inventoryItemId: item._id,
        quantity: quantityNum,
        reason: reason.trim(),
        ...(notes.trim() && { notes: notes.trim() }),
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(extractWastageError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Record wastage
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
          Stock that is gone but was not sold.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {/* labelId wires the label to the listbox trigger. Without it MUI's
              Select renders a combobox with no accessible name — unreachable
              by a screen reader and by anything querying on the label. */}
          <FormControl size="small" fullWidth disabled={locationsLoading}>
            <InputLabel id="wastage-location-label">Location</InputLabel>
            <Select
              labelId="wastage-location-label"
              value={locationId}
              label="Location"
              onChange={e => setLocationId(e.target.value)}
            >
              {locations.map(l => (
                <MenuItem key={l._id} value={l._id}>{locationLabel(l)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            options={searchResults}
            value={item}
            onChange={(_, val) => setItem(val)}
            onInputChange={(_, val) => handleSearch(val)}
            getOptionLabel={(o) => o.displayName ?? ''}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            loading={searchLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Item"
                placeholder="Search inventory…"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress size={14} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              label={isRecipe ? 'Servings' : 'Quantity'}
              type="number"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              slotProps={{ htmlInput: { min: 0, step: 'any' } }}
              sx={{ width: 140 }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="wastage-reason-label">Reason</InputLabel>
              <Select
                labelId="wastage-reason-label"
                value={reason}
                label="Reason"
                onChange={e => setReason(e.target.value)}
              >
                {DEFAULT_REASONS.map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {effectHint && (
            <Typography variant="caption" color="text.secondary">
              {effectHint}
            </Typography>
          )}

          <TextField
            size="small"
            label="Notes"
            placeholder="What happened?"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Recording…' : 'Record wastage'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordWastageModal;
